import { useCallback } from 'react';
import { useAuth, useSpectateMatch } from '@shootout-comps/core';
import type { Match } from '@shootout-comps/types';
import { useWatchMatchSocket, useConnectionStatus } from '../adapters/webSocket';
import MatchCard from './MatchCard';

interface LiveMatchCardProps {
  match: Match;
  tournamentId: string;
  onSelect: (matchId: string) => void;
  isHandicap?: boolean;
  sixReds?: boolean;
  onEdit?: (matchId: string) => void;
}

// Wraps MatchCard for a currently-ACTIVE match with a live read-only subscription to that
// match's own socket room, so score/frame-tally/elapsed-time update ball-by-ball instead of
// only refreshing on the coarse tournament:updated event (which only fires on frame/match
// completion). Only ever mounted for active matches — see BracketPage.tsx's per-card
// branching, which is what actually controls this subscription's lifetime.
export default function LiveMatchCard({ match, tournamentId, onSelect, isHandicap, sixReds, onEdit }: LiveMatchCardProps) {
  const { getAccessToken } = useAuth();
  const matchId = match._id;
  // Must be a stable reference (not an inline arrow function) — useSpectateMatch's fetch
  // effect depends on it transitively via fetchMatch's own useCallback, so a fresh function
  // identity every render would re-trigger the fetch on every render, forming an infinite
  // fetch loop that floods the API and trips its rate limiter.
  const matchFetchPath = useCallback(() => `/matches/${matchId}`, [matchId]);
  const sm = useSpectateMatch({
    matchId,
    tournamentId,
    apiBaseUrl: '',
    getAccessToken,
    useWatchMatchSocket,
    useConnectionStatus,
    // Hits the authenticated, owner-scoped endpoint rather than the public-spectate one —
    // the tournament being managed here need not be public.
    matchFetchPath,
  });

  // Read the running score from liveState (sm.ls), NOT the top-level match.score1/score2 —
  // those only ever get set once, at completion (see completeFrame/PUT :matchId/result in
  // match.routes.ts); they stay null for the entire duration of an active match, which is
  // why the live score display was stuck showing 0 the whole way through a frame.
  const live = sm.match && sm.ls ? {
    score1: sm.ls.score1 ?? 0,
    score2: sm.ls.score2 ?? 0,
    frameWins1: sm.frameWins1,
    frameWins2: sm.frameWins2,
    elapsedSeconds: sm.elapsed,
    matchDurationSeconds: sm.matchDuration,
    clockPaused: sm.clockPaused,
    isStandard: sm.isStandard,
    currentFrame: sm.currentFrame,
  } : null;

  return <MatchCard match={match} onSelect={onSelect} isHandicap={isHandicap} sixReds={sixReds} onEdit={onEdit} live={live} />;
}
